/**
 * Firestore path builders for the bandit framework. These are the single
 * source of truth for the collection layout; the security rules block in
 * `src/tooling/firebase-config/firestore/default/firestore.rules` mirrors it.
 *
 * Layout (all under one top-level `bandits` collection, keyed by course):
 *
 *   bandits/{courseId}/experiments/{expId}              posterior snapshot (function-written)
 *   bandits/{courseId}/experiments/{expId}/control/doc  lifecycle (admin-written)
 *   bandits/{courseId}/experiments/{expId}/history/{ts} per-refresh time series (function-written)
 *   bandits/{courseId}/experiments/{expId}/events/{uid} ground truth (owner-written)
 */

/** Identifies one experiment. */
export interface ExperimentRef {
  courseId: string;
  experimentId: string;
}

/**
 * Path of the posterior snapshot doc.
 *
 * @param ref - Experiment identity.
 * @return Slash-separated Firestore document path.
 */
export function banditSnapshotDocPath(ref: ExperimentRef): string {
  return `bandits/${ref.courseId}/experiments/${ref.experimentId}`;
}

/**
 * Path of the admin-written lifecycle control doc.
 *
 * @param ref - Experiment identity.
 * @return Slash-separated Firestore document path.
 */
export function banditControlDocPath(ref: ExperimentRef): string {
  return `${banditSnapshotDocPath(ref)}/control/doc`;
}

/**
 * Path of the history collection (one doc per refresh).
 *
 * @param ref - Experiment identity.
 * @return Slash-separated Firestore collection path.
 */
export function banditHistoryCollectionPath(ref: ExperimentRef): string {
  return `${banditSnapshotDocPath(ref)}/history`;
}

/**
 * Path of the events collection (one doc per exposed student).
 *
 * @param ref - Experiment identity.
 * @return Slash-separated Firestore collection path.
 */
export function banditEventsCollectionPath(ref: ExperimentRef): string {
  return `${banditSnapshotDocPath(ref)}/events`;
}

/**
 * Path of one student's ground-truth event doc.
 *
 * @param ref - Experiment identity.
 * @param uid - The student's auth uid (doubles as the document ID, which is
 *   what bounds each student to a single observation).
 * @return Slash-separated Firestore document path.
 */
export function banditEventDocPath(ref: ExperimentRef, uid: string): string {
  return `${banditEventsCollectionPath(ref)}/${uid}`;
}
